import Markdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

const components: Components = {
  h2: ({ children }) => <h2 className="text-sm font-semibold text-ink">{children}</h2>,
  h3: ({ children }) => <h3 className="text-label font-semibold text-ink">{children}</h3>,
  h4: ({ children }) => <h4 className="text-label font-semibold text-ink">{children}</h4>,
  h5: ({ children }) => <h5 className="text-label font-semibold text-ink">{children}</h5>,
  h6: ({ children }) => <h6 className="text-label font-semibold text-ink">{children}</h6>,
  ul: ({ children }) => (
    <ul className="list-disc space-y-1.5 ps-5 marker:text-ink-faint [&>ul]:mt-1.5 [&>ol]:mt-1.5">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal space-y-1.5 ps-5 marker:text-ink-faint [&>ul]:mt-1.5 [&>ol]:mt-1.5">
      {children}
    </ol>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-line-strong pl-3 text-ink-faint">
      {children}
    </blockquote>
  ),
  a: ({ children, node: _node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      className="text-accent-ink underline underline-offset-2 hover:text-accent"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hr: () => <hr className="border-line" />,
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-[var(--radius-field)] bg-surface-sunken p-3 font-mono text-micro">
      {children}
    </pre>
  ),
  code: ({ children }) => (
    <code className="font-mono text-micro text-ink">{children}</code>
  ),
  /* The nutrition tables are wider than the peek drawer, so they scroll. */
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-micro">{children}</table>
    </div>
  ),
  /* `text-left` is the default; GFM `---:` columns override it inline. */
  th: ({ children, node: _node, ...props }) => (
    <th {...props} className="px-2 py-1.5 text-left font-medium whitespace-nowrap text-ink-faint">
      {children}
    </th>
  ),
  td: ({ children, node: _node, ...props }) => (
    <td {...props} className="px-2 py-1.5 align-top text-ink-soft">
      {children}
    </td>
  ),
  tr: ({ children }) => <tr className="border-b border-line last:border-b-0">{children}</tr>,
  /*
   * Instructions have no images and the app stores photos in `image_url`, so a
   * markdown image becomes a plain link rather than a third-party request.
   */
  img: ({ src, alt }) => (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="text-accent-ink underline underline-offset-2 hover:text-accent"
    >
      {alt || src}
    </a>
  ),
}

/**
 * Render stored markdown as read-only content, styled from the design tokens.
 *
 * Raw HTML in the source is not rendered, and `react-markdown` sanitises URLs,
 * so instructions cannot inject markup or scripts. Block spacing comes from
 * `space-y` on the wrapper, which is why the children carry no vertical margins.
 */
export function MarkdownView({ children }: { children: string }) {
  return (
    <div className="space-y-3 text-sm text-ink-soft text-pretty">
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </Markdown>
    </div>
  )
}